import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useMemo } from "react"
import { apiClientV2 } from "../../../services/api/apiClient"
import { SELECTION_STALE_TIME } from "./articlesApi"
import {
  fingerprintIds,
  mcCursorList,
  mcCursorListKeepPrevious,
  postIdsInChunks,
  useMcPatch,
} from "./mcQueryHelpers"
import type {
  ArticleRow,
  ComplianceEventOut,
  SupplierCreateIn,
  SupplierOut,
  SupplierRow,
  SupplierUpdateIn,
} from "./types/masterData"

const BASE = "/material-compliance/master-data/suppliers"

// ─── List ─────────────────────────────────────────────────────────────────────

// Mirrors backend SUPPLIER_BUCKETS (mc_supplier_bucket_predicates.py): the
// dashboard tiles' supplier buckets, accepted by GET /suppliers?bucket=. The
// backend 400s on anything else, so URL values are coerced here first: a
// stale bookmark must degrade to "no bucket filter", never break the list.
export const SUPPLIER_BUCKETS = [
  "in_review",
  "awaiting",
  "outdated",
  "never_asked",
  "window_ok",
] as const
export type SupplierBucket = (typeof SUPPLIER_BUCKETS)[number]

export function toSupplierBucket(
  raw: string | null | undefined,
): SupplierBucket | undefined {
  return (SUPPLIER_BUCKETS as readonly string[]).includes(raw ?? "")
    ? (raw as SupplierBucket)
    : undefined
}

export function useSupplierList(
  params: {
    q?: string
    product_id?: string
    customer_id?: string
    article_id?: string
    substance_id?: string
    status?: string
    /** Supplier Studio dashboard bucket deep-link (e.g. "outdated"), see SUPPLIER_BUCKETS. */
    bucket?: SupplierBucket
    sort?: string
    limit?: number
    /** Set false to skip the request entirely, e.g. while a sibling query owns the active view. Defaults to true. */
    enabled?: boolean
  } = {},
) {
  const { enabled = true, ...listParams } = params
  return useInfiniteQuery(
    mcCursorListKeepPrevious<SupplierRow>(
      ["mc", "suppliers", "list", listParams],
      BASE,
      listParams,
      enabled,
    ),
  )
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export function useSupplierDetail(id: string | null) {
  return useQuery({
    queryKey: ["mc", "suppliers", "detail", id],
    queryFn: () =>
      apiClientV2.get<SupplierOut>(`${BASE}/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

// ─── Selection resolve (checkbox selection → display rows) ────────────────────

/** Must stay ≤ the backend's SUPPLIER_RESOLVE_MAX_IDS. */
export const SUPPLIER_RESOLVE_CHUNK = 1000

export interface ResolvedSupplier {
  id: string
  name: string
  supplierNumber: string | null
  country: string | null
  articlesCount: number
  /** Warning notes on the supplier; the backend omits it with MC_NOTES off. */
  activeWarningCount?: number
}

/**
 * The selected suppliers' names and article counts, resolved server-side
 * instead of by paging the supplier list until every selected id is loaded.
 * Pass a MEMOIZED array (the query key is a fingerprint of the ids).
 */
export function useResolveSuppliers(supplierIds: string[], enabled = true) {
  // Skipped while disabled, same as useResolveArticles: the sibling flow owns
  // the view, and hashing its whole selection for a query that never runs is
  // exactly the work the opt-out exists to avoid.
  const idsKey = useMemo(
    () => (enabled ? fingerprintIds(supplierIds) : ""),
    [supplierIds, enabled],
  )
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- idsKey IS the identity of supplierIds
  return useQuery({
    queryKey: ["mc", "suppliers", "resolve", idsKey],
    queryFn: ({ signal }) =>
      postIdsInChunks<ResolvedSupplier>(
        `${BASE}/resolve`,
        "supplierIds",
        supplierIds,
        SUPPLIER_RESOLVE_CHUNK,
        signal,
      ),
    enabled: enabled && supplierIds.length > 0,
    staleTime: SELECTION_STALE_TIME,
    // See SELECTION_STALE_TIME: staleTime only defers a refocus refetch, it
    // doesn't prevent one.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // See useArticleSupplierGroups: removing a recipient changes this key, and
    // the list must not blank out between the click and the refetch.
    placeholderData: keepPreviousData,
  })
}

// ─── Sub-resource: articles from this supplier ────────────────────────────────

export function useSupplierArticles(
  id: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ArticleRow>(
      ["mc", "suppliers", id, "articles", params],
      `${BASE}/${id}/articles`,
      params,
      !!id,
    ),
  )
}

// ─── Sub-resource: audit trail ────────────────────────────────────────────────

export function useSupplierAudit(
  id: string | null,
  params: { limit?: number } = {},
) {
  return useInfiniteQuery(
    mcCursorList<ComplianceEventOut>(
      ["mc", "suppliers", id, "audit", params],
      `${BASE}/${id}/audit`,
      params,
      !!id,
    ),
  )
}

// ─── Mutation ─────────────────────────────────────────────────────────────────

export function usePatchSupplier() {
  return useMcPatch<SupplierOut, SupplierUpdateIn>("suppliers", BASE)
}

// ─── Create (MC_DECL_V2_SUP: creating a supplier straight from declaration review) ──

/**
 * POST /master-data/suppliers's 409 detail, as it ACTUALLY arrives — snake_case,
 * because apiClientV2's response interceptor only camelCases the success path
 * (see declarationInquiriesApi.ts's SendDeclarationInquiryErrorDetail and
 * approveGateTypes.ts's ApproveGateDetail for the identical convention on this
 * codebase's other 409/422 bodies). `supplier_id` names the EXISTING supplier
 * that already holds the requested number — the caller's "assign instead"
 * escape hatch, not the row that just failed to create.
 */
export type CreateSupplierErrorCode = "SUPPLIER_NUMBER_TAKEN" | string

export interface CreateSupplierErrorDetail {
  code: CreateSupplierErrorCode
  message?: string
  supplier_id?: string
}

/** Reads the 409's `detail` off an axios-shaped error, or null when the error
 *  carries none (a network failure, a 5xx already toasted by the global
 *  interceptor, …) — mirrors `approveGateDetailOf` in approveGateTypes.ts. */
export function createSupplierErrorDetailOf(
  error: unknown,
): CreateSupplierErrorDetail | null {
  const detail = (
    error as { response?: { data?: { detail?: CreateSupplierErrorDetail } } }
  )?.response?.data?.detail
  return detail?.code ? detail : null
}

/**
 * Creates a master-data supplier. NO onError toast here, deliberately: a
 * >=500 is already toasted once by the global interceptor (mirrors
 * `toastApiError`'s own dedupe in apiError.ts), and the one
 * business error this endpoint defines (409 SUPPLIER_NUMBER_TAKEN) is always
 * rendered INLINE by the caller (SupplierAssignControl's own conflict panel,
 * with the "assign instead" escape hatch) rather than a toast — a toast alone
 * would lose `detail.supplier_id` the moment it faded.
 */
export function useCreateSupplier() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (input: SupplierCreateIn): Promise<SupplierOut> =>
      apiClientV2.post<SupplierOut>(BASE, input).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mc", "suppliers"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}
